import { Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import { Mail, Phone, MapPin, Github } from 'lucide-react'
import { companyService } from '@/services/company'
import { contactService } from '@/services/contact'
import { servicesService } from '@/services/services'
import { useTranslation } from 'react-i18next'

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
)

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const { t } = useTranslation()
  
  // Fetch company info
  const { data: companyInfo } = useQuery('companyInfo', () => companyService.getCompanyInfo())
  
  // Fetch contact info
  const { data: contactInfo } = useQuery('contactInfo', () => contactService.getContactInfo())

  // Fetch services for footer links
  const { data: services = [] } = useQuery(
    'services',
    () => servicesService.getServices({ is_active: true }),
    {
      staleTime: 5 * 60 * 1000,
    }
  )

  const footerLinks = {
    company: [
      { name: t('header.about'), href: '/hakkimizda' },
      { name: t('header.services'), href: '/hizmetler' },
      { name: t('header.projects'), href: '/projeler' },
      { name: t('header.blog'), href: '/blog' },
    ],
    services: services.slice(0, 5).map(service => ({
      name: service.title,
      href: `/hizmetler/${service.slug || service.id}`
    })),
    resources: [
      { name: t('header.contact'), href: '/iletisim' },
      { name: t('footer.privacyPolicy'), href: '/gizlilik-politikasi' },
      { name: t('footer.termsOfService'), href: '/kullanim-sartlari' },
    ],
  }

  // Sosyal medya linkleri
  const socialLinks = [
    { name: 'WhatsApp', href: 'https://wa.me/905324717522', isWhatsApp: true, icon: null },
    { name: 'GitHub', icon: Github, href: 'https://github.com/crvsoftware', isWhatsApp: false }
  ] as const

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container-custom">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-6">
              <img 
                src="/logo.png" 
                alt={companyInfo?.name || 'CRV Software'} 
                className="h-8 w-auto" 
              />
              <span className="text-xl font-display font-bold">{companyInfo?.name || 'CRV Software'}</span>
            </Link>
            <p className="text-gray-300 mb-6 leading-relaxed">
              {contactInfo?.description || companyInfo?.description || 'Teknolojiyi geleceğe taşıyoruz. Modern yazılım çözümleri ile işinizi dijital dünyaya taşıyoruz.'}
            </p>
            <div className="space-y-3">
              {(contactInfo?.email || companyInfo?.email) && (
                <div className="flex items-center space-x-3 text-gray-300">
                  <Mail className="w-4 h-4" />
                  <a 
                    href={`mailto:${(contactInfo?.email || companyInfo?.email || '').trim()}`}
                    onClick={(e) => {
                      e.preventDefault()
                      window.location.href = `mailto:${(contactInfo?.email || companyInfo?.email || '').trim()}`
                    }}
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    {contactInfo?.email || companyInfo?.email}
                  </a>
                </div>
              )}
              {(contactInfo?.phone || companyInfo?.phone) && (
                <div className="flex items-center space-x-3 text-gray-300">
                  <Phone className="w-4 h-4" />
                  <a 
                    href={`tel:${(contactInfo?.phone || companyInfo?.phone || '').trim()}`}
                    className="hover:text-white transition-colors"
                  >
                    {contactInfo?.phone || companyInfo?.phone}
                  </a>
                </div>
              )}
              {(contactInfo?.address || companyInfo?.address) && (
                <div className="flex items-center space-x-3 text-gray-300">
                  <MapPin className="w-4 h-4" />
                  <span>{contactInfo?.address || companyInfo?.address}</span>
                </div>
              )}
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6">{t('footer.company')}</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6">{t('footer.services')}</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6">{t('footer.resources')}</h3>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-gray-800 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © {currentYear} {companyInfo?.name || 'CRV Software'}. {t('footer.allRightsReserved')}.
            </div>
            {socialLinks.length > 0 && (
              <div className="flex items-center space-x-6">
                {socialLinks.map((social) => {
                  const Icon = social.icon
                  return (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors"
                      aria-label={social.name}
                    >
                      {social.isWhatsApp ? (
                        <WhatsAppIcon className="w-5 h-5" />
                      ) : Icon ? (
                        <Icon className="w-5 h-5" />
                      ) : null}
                    </a>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
